import { useState } from "preact/hooks";
import type { BookmarkTarget, SpaceOut } from "../api/types.gen.ts";
import { t } from "../i18n/index.ts";
import { isBookmarked, setBookmark } from "../lib/bookmarks.ts";
import { ignore } from "../lib/ignore.ts";
import { outsideOf } from "../lib/paths.ts";
import { Button, IconButton } from "./Button.tsx";
import { BookmarkedIcon, BookmarkIcon } from "./icons.tsx";
import type { IconSize, IconVariant } from "./styles.ts";

/** Out of sight until its row is hovered, or it has the keyboard's focus. */
const QUIET = "opacity-0 group-hover:opacity-100 focus-visible:opacity-100";

/**
 * Bookmarks `target` for you, or takes the bookmark off (pressed while it's bookmarked): an icon, or with `text` an
 * icon and words. Nothing from outside the space (a public or guest page): bookmarks are the space's members'.
 */
export function BookmarkButton(p: {
  space: SpaceOut;
  target: BookmarkTarget;
  size?: IconSize;
  variant?: IconVariant;
  /** Words beside the icon (not bookmarked yet; once it is, "Bookmarked"). */
  text?: string;
  /** In a row: shown while it's bookmarked, else only when the row (a `group`) is hovered or it has the focus. */
  quiet?: boolean;
  class?: string;
}) {
  const [busy, setBusy] = useState(false);
  if (outsideOf(p.space)) {
    return null;
  }
  const on = isBookmarked(p.space.id, p.target);
  const toggle = () => {
    setBusy(true);
    setBookmark(p.space.id, p.target, !on).then(() => setBusy(false), ignore);
  };
  const icon = on ? <BookmarkedIcon class="text-blue-600" /> : <BookmarkIcon />;
  if (p.text) {
    return (
      <Button
        variant="ghost"
        size="sm"
        aria-pressed={on}
        disabled={busy}
        onClick={toggle}
        title={on ? t("bookmarks.remove") : undefined}
        class={p.class}
      >
        {icon}
        {on ? t("bookmarks.bookmarked") : p.text}
      </Button>
    );
  }
  return (
    <IconButton
      label={t(on ? "bookmarks.remove" : "bookmarks.add")}
      aria-pressed={on}
      size={p.size ?? "sm"}
      variant={p.variant ?? "ghost"}
      disabled={busy}
      onClick={toggle}
      class={`${p.quiet && !on ? QUIET : ""} ${p.class ?? ""}`}
    >
      {icon}
    </IconButton>
  );
}
